import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ThemeLogo } from '@/components/theme-logo';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useI18n } from '@/hooks/use-i18n';
import { Loader2 } from 'lucide-react';
import QRCode from 'qrcode';

export default function DesktopLandingPage() {
  const { t } = useI18n();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Generate QR code for the current URL (mobile users will access the same URL)
    const generateQR = async () => {
      try {
        const currentUrl = window.location.origin;
        const qrDataUrl = await QRCode.toDataURL(currentUrl, {
          width: 256,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
        setQrCodeUrl(qrDataUrl);
      } catch (err) {
        console.error('Error generating QR code:', err);
      } finally {
        setIsLoading(false);
      }
    };
    generateQR();
  }, []);

  return (
    <div className='min-h-screen bg-background flex flex-col'>
      {/* Header */}
      <header className='w-full border-b'>
        <div className='container mx-auto p-6'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <ThemeLogo className='h-10 w-auto' />
            </div>
            <div className='flex items-center gap-3'>
              <LanguageSwitcher />
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='flex-grow flex items-center justify-center p-6'>
        <div className='w-full max-w-sm'>
          <Card>
            <CardHeader className='text-center'>
              <CardTitle className='text-xl'>
                {t('desktop.landing.title', 'Acceso a la Aplicación Móvil')}
              </CardTitle>
              <CardDescription>
                {t(
                  'desktop.landing.description',
                  'Esta aplicación está diseñada para su uso en dispositivos móviles. Escanee el código QR para acceder a la PWA optimizada para la mejor experiencia.'
                )}
              </CardDescription>
            </CardHeader>

            <CardContent className='flex flex-col gap-6'>
              {/* QR Code */}
              <div className='flex justify-center'>
                <div className='w-64 h-64 bg-background border rounded-lg p-4'>
                  {isLoading ? (
                    <div className='w-full h-full flex items-center justify-center'>
                      <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                    </div>
                  ) : qrCodeUrl ? (
                    <img
                      src={qrCodeUrl}
                      alt='QR Code'
                      className='w-full h-full'
                    />
                  ) : (
                    <div className='w-full h-full flex items-center justify-center'>
                      <p className='text-sm text-muted-foreground'>
                        {t(
                          'desktop.landing.qrError',
                          'Error al generar código QR'
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* PWA Info */}
              <div className='border-l-4 border-primary bg-muted p-4 rounded-md'>
                <p className='text-sm text-foreground'>
                  <strong>{t('desktop.landing.note', 'Nota:')}</strong>{' '}
                  {t(
                    'desktop.landing.pwaInfo',
                    'La App V16 Compliance es una Progressive Web App (PWA), lo que significa que puede instalarla en su móvil para un acceso rápido y funcionalidades offline.'
                  )}
                </p>
              </div>

              {/* Instructions */}
              <div className='grid gap-3'>
                <p className='text-sm font-medium text-foreground'>
                  {t(
                    'desktop.landing.instructions',
                    'Para acceder desde su móvil:'
                  )}
                </p>
                <ol className='text-sm text-muted-foreground grid gap-2'>
                  <li>
                    1.{' '}
                    {t('desktop.landing.step1', 'Abra la cámara de su móvil')}
                  </li>
                  <li>
                    2. {t('desktop.landing.step2', 'Escanee el código QR')}
                  </li>
                  <li>
                    3.{' '}
                    {t(
                      'desktop.landing.step3',
                      'Abra el enlace en su navegador'
                    )}
                  </li>
                  <li>
                    4.{' '}
                    {t(
                      'desktop.landing.step4',
                      'Instale la app cuando se le solicite'
                    )}
                  </li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className='w-full border-t'>
        <div className='container mx-auto p-6'>
          <p className='text-center text-sm text-muted-foreground'>
            © {new Date().getFullYear()} TrackV16.{' '}
            {t('desktop.landing.rights', 'Todos los derechos reservados.')}
          </p>
        </div>
      </footer>
    </div>
  );
}
